import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Observable, takeUntil } from 'rxjs';

import { BaseComponent } from '../../../../core/components/base/base.component';
import {
  PRACTICE_ENTRY_TYPE_LABELS,
  PracticeEntryForReview,
  describeReferralFailure,
  practiceEntryBlocker,
  reviewWarning,
} from '../../../../core/models/referral.model';
import { alsoAddLabel } from '../../../../core/models/patient-app-history';
import { FeedbackService } from '../../../../core/services/feedback.service';
import { ReferralService } from '../../../../core/services/referral.service';
import { PatientAppService } from '../../../../core/services/patient-app.service';

export interface ReferralPracticeEntryDialogData {
  entryId: string;
  /** The practice whose inbox the row came from. */
  groupId: string;
  patientName: string | null;
  /**
   * 'patient-app': an entry the PATIENT shared from their app (tasks/patient-app.md §6.5, P19), reviewed in the same
   * dialog: filed as a note tagged "Patient provided". Default: an entry another practice shared.
   */
  source?: 'referral' | 'patient-app';
}

export interface ReferralPracticeEntryDialogResult {
  changed?: boolean;
}

/** Dismiss reasons are kept with the entry; the server keeps 2000 characters. */
export const PRACTICE_ENTRY_DISMISS_MAX = 2000;

/**
 * "Validate to timeline" for an entry another MedicAI practice shared on a referral this practice accepted
 * (referrals architecture §19, P7, D22): the inbox opener for `referral-practice-entry`.
 *
 * Shows the entry exactly as it will be filed (a read-only note: title, text, date, flags, and its copied files) and where
 * it came from. Validate files it on the patient the referral was accepted as; dismiss keeps it out, with a reason.
 */
@Component({
  selector: 'app-referral-practice-entry-dialog',
  templateUrl: './referral-practice-entry-dialog.component.html',
  styleUrls: ['../inbox-review.scss'],
})
export class ReferralPracticeEntryDialogComponent extends BaseComponent implements OnInit {
  entry: PracticeEntryForReview | null = null;
  isLoading = true;
  loadError: string | null = null;
  isSaving = false;
  actionError: string | null = null;
  dismissing = false;
  dismissReason = '';
  /** "Also add to Allergies" (or Medical history, Medications) for an entry from the patient app: on unless unticked. */
  alsoAddToList = true;

  readonly typeLabels = PRACTICE_ENTRY_TYPE_LABELS;
  readonly dismissMax = PRACTICE_ENTRY_DISMISS_MAX;

  constructor(
    private referralService: ReferralService,
    private patientAppService: PatientAppService,
    private dialogRef: MatDialogRef<ReferralPracticeEntryDialogComponent, ReferralPracticeEntryDialogResult>,
    @Inject(MAT_DIALOG_DATA) public data: ReferralPracticeEntryDialogData,
    fs: FeedbackService,
    md: MatDialog,
  ) {
    super(fs, md);
  }

  get fromPatientApp(): boolean {
    return this.data.source === 'patient-app';
  }

  ngOnInit(): void {
    const load$ = this.fromPatientApp
      ? this.patientAppService.getInboundEntry(this.data.entryId, this.data.groupId)
      : this.referralService.getPracticeEntry(this.data.entryId, this.data.groupId);
    (load$ as any).pipe(takeUntil(this.destroy$)).subscribe({
      next: (entry: PracticeEntryForReview) => {
        this.entry = entry;
        this.isLoading = false;
      },
      error: (error: unknown) => {
        this.isLoading = false;
        this.loadError = describeReferralFailure(error, 'This entry could not be loaded.');
      },
    });
  }

  /** A patient's file, opened for a moment before it is filed (a 10-minute link from quarantine). */
  previewFile(fileId: string): void {
    if (!this.fromPatientApp) return;
    this.patientAppService.previewInboundFile(this.data.entryId, fileId, this.data.groupId).pipe(takeUntil(this.destroy$)).subscribe({
      next: ({ url }) => window.open(url, '_blank', 'noopener'),
      error: () => this.showToastMessage('This file could not be opened.'),
    });
  }

  get blocker(): string | null {
    return this.entry ? practiceEntryBlocker(this.entry) : null;
  }

  get warning(): string | null {
    return this.entry ? reviewWarning({ isMorbidity: this.entry.rendered.isMorbidity, isMortality: this.entry.rendered.isMortality }) : null;
  }

  /** The "Also add to ..." label when this patient app entry belongs in one of the patient's lists. */
  get alsoAdd(): string | null {
    return this.fromPatientApp ? alsoAddLabel((this.entry as { listTarget?: string | null } | null)?.listTarget) : null;
  }

  get failedFiles(): number {
    return this.entry ? this.entry.files.filter((f) => f.status === 'failed').length : 0;
  }

  validate(): void {
    if (!this.entry || this.blocker || this.isSaving) return;
    this.isSaving = true;
    this.actionError = null;
    const validate$: Observable<{ patientNoteId: string; alreadyFiled: boolean; filedToList?: string | null }> = this.fromPatientApp
      ? this.patientAppService.validateInboundEntry(this.data.entryId, this.data.groupId, !!this.alsoAdd && this.alsoAddToList)
      : this.referralService.validatePracticeEntry(this.data.entryId, this.data.groupId);
    validate$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (result: { patientNoteId: string; alreadyFiled: boolean; filedToList?: string | null }) => {
        this.isSaving = false;
        this.showToastMessage(result.filedToList === 'already_recorded'
          ? 'Validated: the entry is on the patient\'s timeline. Their list already held it.'
          : 'Validated: the entry is on the patient\'s timeline.');
        this.dialogRef.close({ changed: true });
      },
      error: (error) => {
        this.isSaving = false;
        this.actionError = describeReferralFailure(error, 'The entry could not be validated. Please try again.');
      },
    });
  }

  confirmDismiss(): void {
    const reason = this.dismissReason.trim();
    if (!reason || this.isSaving) return;
    this.isSaving = true;
    this.actionError = null;
    const dismiss$ = this.fromPatientApp
      ? this.patientAppService.dismissInboundEntry(this.data.entryId, reason, this.data.groupId)
      : this.referralService.dismissPracticeEntry(this.data.entryId, reason, this.data.groupId);
    dismiss$.pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.isSaving = false;
        this.showToastMessage('Entry dismissed.');
        this.dialogRef.close({ changed: true });
      },
      error: (error) => {
        this.isSaving = false;
        this.actionError = describeReferralFailure(error, 'The entry could not be dismissed. Please try again.');
      },
    });
  }

  close(): void {
    this.dialogRef.close({});
  }
}
